import { AfterViewInit, Component, computed, EventEmitter, Inject, inject, Input, OnDestroy, Optional, Output, signal, Type, WritableSignal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { LatLngBounds, latLngBounds } from 'leaflet';
import { LayerService } from '../services/layer.service';
import { isPointInPolygon, calculatePolygonArea, calculateBoundsArea } from '../utils';
import { TransformationService } from '../services/transformation.service';
import { OfflineModeService } from '../../services/offline-mode.service';
import { FILTER_PROVIDER, FilterProvider, FILTER_COMPONENT, DATA_PROVIDER, DataProvider, PROJECT_PROVIDER, ProjectProvider, CONFIRM_SERVICE, IConfirmService, AUTH_PROVIDER, AuthProvider } from '../../core';
import { MAP_NAVIGATION_HANDLER, MapNavigationHandler } from '../handlers';
import { BoundsSelection } from '../project/project-points.component';
import { Subject, takeUntil } from 'rxjs';

interface RegionItem {
	index: number;
	bounds: LatLngBounds;
	active: boolean;
	pointCount: number;
	filterName?: string;
}

@Component({
	selector: 'bp-point-store',
	templateUrl: './point-store.component.html',
	styleUrls: ['./point-store.component.css', '../accordion.css'],
	standalone: true,
	imports: [CommonModule, FormsModule]
})
export class PointStoreComponent implements AfterViewInit, OnDestroy {
	activeModal = inject(NgbActiveModal);
	private navigationHandler = inject(MAP_NAVIGATION_HANDLER, { optional: true }) as MapNavigationHandler | null;
	private destroy$ = new Subject<void>();

	/** Modus: 'points' zeigt Filter+Rechteckauswahl, 'regions' zeigt Projektbereiche */
	@Input() mode: 'points' | 'regions' = 'points';

	// Parent-Signals (werden über setParentSignals gesetzt, mit Defaults)
	private _boundsSelections: WritableSignal<BoundsSelection[]> = signal([]);
	private _activeRegionIndices: WritableSignal<number[]> = signal([]);
	private _regionPointIds: WritableSignal<string[]> = signal([]);
	private _regionFilters: WritableSignal<Record<number, string>> = signal({});
	private _regionNames: WritableSignal<Record<number, string>> = signal({});
	private _globalFilterName: WritableSignal<string | null> = signal(null);
	private _useGlobalFilter: WritableSignal<boolean> = signal(false);

	// Getter für Template-Binding (delegieren an Parent-Signals)
	get boundsSelections() { return this._boundsSelections; }
	get activeRegionIndices() { return this._activeRegionIndices; }
	get regionPointIds() { return this._regionPointIds; }
	get regionFilters() { return this._regionFilters; }
	get regionNames() { return this._regionNames; }
	get globalFilterName() { return this._globalFilterName; }
	get useGlobalFilter() { return this._useGlobalFilter; }

	/** Wird vom Parent aufgerufen um die Signal-Referenzen zu setzen */
	setParentSignals(
		boundsSelections: WritableSignal<BoundsSelection[]>,
		activeRegionIndices: WritableSignal<number[]>,
		regionPointIds: WritableSignal<string[]>,
		regionFilters: WritableSignal<Record<number, string>>,
		regionNames: WritableSignal<Record<number, string>>,
		globalFilterName: WritableSignal<string | null>,
		useGlobalFilter: WritableSignal<boolean>
	) {
		this._boundsSelections = boundsSelections;
		this._activeRegionIndices = activeRegionIndices;
		this._regionPointIds = regionPointIds;
		this._regionFilters = regionFilters;
		this._regionNames = regionNames;
		this._globalFilterName = globalFilterName;
		this._useGlobalFilter = useGlobalFilter;
	}

	// Event wenn Filter geändert werden (für sofortiges Speichern)
	@Output() filtersChanged = new EventEmitter<void>();

	// Event für GeoJSON-Import mit ausgewählten Dateien (wird vom Parent behandelt)
	@Output() geoJsonImportRequested = new EventEmitter<File[]>();

	// Lokale Signals
	regionItems = signal<RegionItem[]>([]);
	allPointsCount = signal<number>(0);
	availableFilters = signal<string[]>([]);
	boundsFilteredCounts = signal<Map<number, number>>(new Map());  // Index -> gefilterte Anzahl
	ready = signal(false);  // Wird true wenn Parent-Signals gesetzt sind
	private initialized = false;

	// Cache für queryIndex() um mehrfache Server-Anfragen zu vermeiden
	private punkteCache: { rows: any[] } | null = null;

	// Computed für Gesamtzahl aktiver Bounds-Punkte (gefiltert)
	totalBoundsCount = computed(() => {
		const counts = this.boundsFilteredCounts();
		return this.boundsSelections()
			.filter(s => s.active)
			.reduce((sum, s, i) => sum + (counts.get(i) ?? s.pointIds.length), 0);
	});

	// Computed für die Breite der Flächenspalte (basierend auf dem breitesten Wert)
	areaColumnWidth = computed(() => {
		const items = this.regionItems();
		if (items.length === 0) return '50px';

		let maxLength = 0;
		for (const item of items) {
			const areaStr = this.getRegionAreaHa(item);
			if (areaStr.length > maxLength) {
				maxLength = areaStr.length;
			}
		}
		// ~8px pro Zeichen + 20px für " ha"
		const width = Math.max(50, maxLength * 8 + 25);
		return `${width}px`;
	});

	constructor(
		public ls: LayerService,
		public oms: OfflineModeService,
		@Inject(PROJECT_PROVIDER) public ps: ProjectProvider,
		@Inject(AUTH_PROVIDER) public aus: AuthProvider,
		@Inject(DATA_PROVIDER) private ds: DataProvider,
		private ts: TransformationService,
		@Inject(FILTER_PROVIDER) private fs: FilterProvider,
		private modalService: NgbModal,
		@Inject(CONFIRM_SERVICE) private confirmService: IConfirmService,
		@Optional() @Inject(FILTER_COMPONENT) private filterComponentType: Type<any> | null
	) {
		// Bei Filter-Änderungen: Zählungen neu berechnen
		this.fs.apply.pipe(takeUntil(this.destroy$)).subscribe(() => this.onFilterChanged());
		this.fs.filtersUpdated.pipe(takeUntil(this.destroy$)).subscribe(() => this.onFilterChanged());
	}

	ngOnDestroy() {
		this.destroy$.next();
		this.destroy$.complete();
	}

	/**
	 * Wird aufgerufen, wenn ein Filter geändert wurde.
	 * Berechnet alle gefilterten Zählungen neu.
	 */
	private async onFilterChanged() {
		if (!this.initialized) return;
		await this.loadBoundsFilteredCounts();
		await this.loadRegionItems();
	}

	async ngAfterViewInit() {
		// Warten bis Parent die Inputs gesetzt hat
		await Promise.resolve();
		if (!this.initialized) {
			await this.initialize();
		}
	}

	/** Kann auch manuell vom Parent aufgerufen werden */
	async initialize() {
		if (this.initialized) return;
		this.initialized = true;
		try {
			await this.loadAvailableFilters();
			await this.loadRegionItems();
			await this.loadBoundsFilteredCounts();
			await this.loadAllPointsCount();
		} catch (err) {
			console.warn('PointStoreComponent initialize fehlgeschlagen:', err);
		}
		this.ready.set(true);
	}

	private async loadAvailableFilters() {
		const filters = await this.fs.list_keys();
		this.availableFilters.set(filters);
	}

	/** Holt Punkte aus Cache oder lädt sie einmalig aus der DB */
	private async getCachedPunkte(): Promise<{ rows: any[] }> {
		if (this.punkteCache) {
			return this.punkteCache;
		}
		try {
			const response: any = await this.ds.queryIndex();
			this.punkteCache = { rows: response?.rows ?? [] };
		} catch (err) {
			console.warn('getCachedPunkte fehlgeschlagen:', err);
			this.punkteCache = { rows: [] };
		}
		return this.punkteCache;
	}

	private async loadAllPointsCount() {
		const response = await this.getCachedPunkte();
		this.allPointsCount.set(response.rows.length);
	}

	private async loadBoundsFilteredCounts() {
		const selections = this.boundsSelections();
		if (selections.length === 0) {
			this.boundsFilteredCounts.set(new Map());
			return;
		}

		const response = await this.getCachedPunkte();
		const rows = response.rows;
		const rowsById = new Map<string, any>();
		rows.forEach((row: any) => rowsById.set(row.id, row));

		const counts = new Map<number, number>();

		for (let i = 0; i < selections.length; i++) {
			const sel = selections[i];
			// Filter bestimmen: Eigener Filter > Globaler Filter > Kein Filter
			const filterName = sel.filterName ?? (this.useGlobalFilter() ? this.globalFilterName() : null);
			const filterFunc = filterName ? await this.getFilterFunction(filterName) : null;

			if (filterFunc) {
				let count = 0;
				for (const id of sel.pointIds) {
					const row = rowsById.get(id);
					if (row) {
						const header = row.key[4];
						const doc = {
							_id: row.id,
							type: row.key[1],
							header: header,
							state: row.key[5],
							version: row.key[6]
						};
						try {
							if (filterFunc(doc)) count++;
						} catch {
							// Filter konnte nicht angewendet werden - Punkt ausschließen
						}
					}
				}
				counts.set(i, count);
			} else {
				counts.set(i, sel.pointIds.length);
			}
		}

		this.boundsFilteredCounts.set(counts);
	}

	private async loadRegionItems() {
		const regions = this.ls.regions()?.regions ?? [];
		const activeIndices = new Set(this.activeRegionIndices());
		const savedFilters = this.regionFilters();
		const response = await this.getCachedPunkte();
		const rows = response.rows;

		const items: RegionItem[] = [];

		for (let i = 0; i < regions.length; i++) {
			const region = regions[i];
			// Keys können als String gespeichert sein (JSON), daher beide Varianten prüfen
			const savedFilter = savedFilters[i] ?? (savedFilters as any)[String(i)];
			const filterName = savedFilter ?? (this.useGlobalFilter() ? this.globalFilterName() : null);
			let count = 0;

			// Punktanzahl berechnen (mit Filter falls vorhanden)
			const filterFunc = filterName ? await this.getFilterFunction(filterName) : null;

			rows.forEach((row: any) => {
				const header = row.key[4];
				if (header?.koordinaten) {
					const ll = this.ts.from(header.koordinaten);
					// Prüfen ob Punkt in Region liegt
					let isInRegion = false;
					if (region.type === 'polygon' && region.polygon) {
						if (region.bounds.contains(ll)) {
							isInRegion = isPointInPolygon(ll, region.polygon);
						}
					} else {
						isInRegion = region.bounds.contains(ll);
					}

					if (isInRegion) {
						if (filterFunc) {
							// Vollständiges Dokument für Filter aufbauen
							const doc = {
								_id: row.id,
								type: row.key[1],
								header: header,
								state: row.key[5],
								version: row.key[6]
							};
							try {
								if (filterFunc(doc)) {
									count++;
								}
							} catch {
								// Filter konnte nicht angewendet werden - Punkt ausschließen
							}
						} else {
							count++;
						}
					}
				}
			});

			items.push({
				index: i,
				bounds: region.bounds,
				active: activeIndices.has(i),
				pointCount: count,
				filterName: savedFilter
			});
		}

		this.regionItems.set(items);
	}

	// === Rechteckauswahl ===

	selectBoundsPoints() {
		this.activeModal.close();
		// Kurze Verzögerung damit Modal geschlossen ist
		setTimeout(() => {
			this.ls.pointBoundsSelectionRequested.emit();
		}, 100);
	}

	toggleBoundsSelection(sel: BoundsSelection) {
		sel.active = !sel.active;
		this.boundsSelections.set([...this.boundsSelections()]);
	}

	removeBoundsSelection(sel: BoundsSelection) {
		const filtered = this.boundsSelections().filter(s => s !== sel);
		this.boundsSelections.set(filtered);
	}

	renameBoundsSelection(sel: BoundsSelection, name: string) {
		sel.name = name || undefined;
		this.boundsSelections.set([...this.boundsSelections()]);
		this.filtersChanged.emit();
	}

	getBoundsSelectionName(index: number, sel: BoundsSelection): string {
		return sel.name || `Auswahl ${index + 1}`;
	}

	zoomToBoundsSelection(sel: BoundsSelection) {
		if (sel.pointIds.length === 0) return;
		this.zoomToPoints(sel.pointIds);
	}

	// === Regions ===

	toggleRegion(item: RegionItem) {
		item.active = !item.active;
		this.updateActiveRegionIndices();
	}

	toggleAllRegions(active: boolean) {
		const items = this.regionItems();
		items.forEach(item => item.active = active);
		this.regionItems.set([...items]);
		this.updateActiveRegionIndices();
	}

	renameRegion(item: RegionItem, name: string) {
		const names = { ...this.regionNames() };
		if (name) {
			names[item.index] = name;
		} else {
			delete names[item.index];
		}
		this.regionNames.set(names);
		this.filtersChanged.emit();
	}

	getRegionName(item: RegionItem): string {
		const names = this.regionNames();
		return names[item.index] || (names as any)[String(item.index)] || `Bereich ${item.index + 1}`;
	}

	private updateActiveRegionIndices() {
		const activeIndices = this.regionItems()
			.filter(item => item.active)
			.map(item => item.index);
		this.activeRegionIndices.set(activeIndices);
		this.updateRegionPointIds();
	}

	private async updateRegionPointIds() {
		const regions = this.ls.regions()?.regions ?? [];
		const activeIndices = this.activeRegionIndices();
		const items = this.regionItems();

		if (regions.length === 0 || activeIndices.length === 0) {
			this.regionPointIds.set([]);
			this.emitProjectPointsChange();
			return;
		}

		const response = await this.getCachedPunkte();
		const rows = response.rows;
		const inRegionIds: string[] = [];

		for (const i of activeIndices) {
			if (i >= regions.length) continue;
			const region = regions[i];
			const item = items.find(it => it.index === i);

			// Filter bestimmen: Eigener Filter > Globaler Filter > Kein Filter
			const filterName = item?.filterName ?? (this.useGlobalFilter() ? this.globalFilterName() : null);
			const filterFunc = filterName ? await this.getFilterFunction(filterName) : null;

			rows.forEach((row: any) => {
				const header = row.key[4];
				if (header?.koordinaten) {
					const ll = this.ts.from(header.koordinaten);
					// Prüfen ob Punkt in Region liegt
					let isInRegion = false;
					if (region.type === 'polygon' && region.polygon) {
						if (region.bounds.contains(ll)) {
							isInRegion = isPointInPolygon(ll, region.polygon);
						}
					} else {
						isInRegion = region.bounds.contains(ll);
					}

					if (isInRegion) {
						if (filterFunc) {
							// Vollständiges Dokument für Filter aufbauen
							const doc = {
								_id: row.id,
								type: row.key[1],
								header: header,
								state: row.key[5],
								version: row.key[6]
							};
							try {
								if (filterFunc(doc)) {
									inRegionIds.push(row.id);
								}
							} catch {
								// Filter konnte nicht angewendet werden - Punkt ausschließen
							}
						} else {
							inRegionIds.push(row.id);
						}
					}
				}
			});
		}

		this.regionPointIds.set(inRegionIds);
		this.emitProjectPointsChange();
	}

	private async emitProjectPointsChange() {
		// Kombinierte Punkt-IDs berechnen und an Karte senden
		const allIds = new Set<string>();

		// Gefilterte Bounds-Punkte berechnen
		const filteredBoundsIds = await this.getFilteredBoundsPointIds();
		filteredBoundsIds.forEach(id => allIds.add(id));

		// Region-Punkte sind bereits gefiltert
		this.regionPointIds().forEach(id => allIds.add(id));
		this.ls.projectPointsChanged.emit(allIds);
	}

	/**
	 * Gibt gefilterte Punkt-IDs aus aktiven Bounds-Selections zurück
	 */
	private async getFilteredBoundsPointIds(): Promise<string[]> {
		const activeSelections = this.boundsSelections().filter(s => s.active);
		if (activeSelections.length === 0) return [];

		const response = await this.getCachedPunkte();
		const rows = response.rows;
		const rowsById = new Map<string, any>();
		rows.forEach((row: any) => rowsById.set(row.id, row));

		const result: string[] = [];

		for (const sel of activeSelections) {
			// Filter bestimmen: Eigener Filter > Globaler Filter > Kein Filter
			const filterName = sel.filterName ?? (this.useGlobalFilter() ? this.globalFilterName() : null);
			const filterFunc = filterName ? await this.getFilterFunction(filterName) : null;

			for (const id of sel.pointIds) {
				if (filterFunc) {
					const row = rowsById.get(id);
					if (row) {
						const header = row.key[4];
						const doc = {
							_id: row.id,
							type: row.key[1],
							header: header,
							state: row.key[5],
							version: row.key[6]
						};
						try {
							if (filterFunc(doc)) {
								result.push(id);
							}
						} catch {
							// Filter konnte nicht angewendet werden - Punkt ausschließen
						}
					}
				} else {
					result.push(id);
				}
			}
		}

		return result;
	}

	// === Filter-Methoden ===

	private async getFilterFunction(filterName: string): Promise<((doc: any) => boolean) | null> {
		const condExp = await this.fs.get(filterName);
		if (!condExp) return null;

		// Filter ohne Bounds generieren (Bounds-Prüfung erfolgt separat über region.contains)
		const funcStr = condExp.filterFunction(false, false, []);
		let filterFunc: (doc: any) => boolean;
		eval(`filterFunc = ${funcStr}`);
		return filterFunc!;
	}

	setRegionFilter(item: RegionItem, filterName: string | undefined) {
		item.filterName = filterName;
		this.regionItems.set([...this.regionItems()]);

		// In regionFilters speichern
		const filters = { ...this.regionFilters() };
		if (filterName) {
			filters[item.index] = filterName;
		} else {
			delete filters[item.index];
		}
		this.regionFilters.set(filters);

		// Punktzahlen und IDs neu berechnen
		this.recalculateRegionPointCount(item);
		this.updateRegionPointIds();
		this.filtersChanged.emit();
	}

	private async recalculateRegionPointCount(item: RegionItem) {
		const regions = this.ls.regions()?.regions ?? [];
		if (item.index >= regions.length) return;

		const region = regions[item.index];
		const filterName = item.filterName ?? (this.useGlobalFilter() ? this.globalFilterName() : null);
		const filterFunc = filterName ? await this.getFilterFunction(filterName) : null;

		const response = await this.getCachedPunkte();
		const rows = response.rows;
		let count = 0;

		rows.forEach((row: any) => {
			const header = row.key[4];
			if (header?.koordinaten) {
				const ll = this.ts.from(header.koordinaten);
				// Prüfen ob Punkt in Region liegt
				let isInRegion = false;
				if (region.type === 'polygon' && region.polygon) {
					if (region.bounds.contains(ll)) {
						isInRegion = isPointInPolygon(ll, region.polygon);
					}
				} else {
					isInRegion = region.bounds.contains(ll);
				}

				if (isInRegion) {
					if (filterFunc) {
						// Vollständiges Dokument für Filter aufbauen
						const doc = {
							_id: row.id,
							type: row.key[1],
							header: header,
							state: row.key[5],
							version: row.key[6]
						};
						try {
							if (filterFunc(doc)) count++;
						} catch {
							// Filter konnte nicht angewendet werden - Punkt ausschließen
						}
					} else {
						count++;
					}
				}
			}
		});

		item.pointCount = count;
		this.regionItems.set([...this.regionItems()]);
	}

	setBoundsFilter(sel: BoundsSelection, filterName: string | undefined) {
		sel.filterName = filterName;
		this.boundsSelections.set([...this.boundsSelections()]);
		// Gefilterte Punktzahlen neu berechnen
		this.loadBoundsFilteredCounts();
		this.emitProjectPointsChange();
		this.filtersChanged.emit();
	}

	toggleGlobalFilter(enabled: boolean) {
		this.useGlobalFilter.set(enabled);
		// Alle Items neu berechnen (sowohl für Display als auch Punkt-IDs)
		this.loadRegionItems();
		this.loadBoundsFilteredCounts();
		this.updateRegionPointIds();
		this.filtersChanged.emit();
	}

	/** Prüft ob aktive Bereiche oder Rechteckauswahlen vorhanden sind */
	hasProjectSelections(): boolean {
		const hasActiveBounds = this.boundsSelections().some(s => s.active);
		const hasActiveRegions = this.activeRegionIndices().length > 0;
		return hasActiveBounds || hasActiveRegions;
	}

	setGlobalFilter(filterName: string | null) {
		this.globalFilterName.set(filterName);
		// Alle Items neu berechnen (sowohl für Display als auch Punkt-IDs)
		this.loadRegionItems();
		this.loadBoundsFilteredCounts();
		this.updateRegionPointIds();
		this.filtersChanged.emit();
	}

	showFilterInfo() {
		this.confirmService.confirm({
			title: 'Filter-Funktion',
			message: `<strong>Filter erstellen:</strong> Mit dem Trichter-Symbol können Sie Filter nach verschiedenen Kriterien erstellen (z.B. ${this.navigationHandler?.getFilterHelpText() ?? 'Typ, Status, Datum'}).

<strong>Filter pro Bereich anwenden:</strong> In der Spalte "Filter" können Sie für jeden Bereich einen der erstellten Filter auswählen. Die Punktanzahl in der Spalte "Anz." zeigt dann nur noch die Punkte an, die dem Filter entsprechen.

<strong>Auswirkungen:</strong>
• <strong>Offline-Sync:</strong> Nur gefilterte Punkte werden synchronisiert
• <strong>Projektzuordnung:</strong> Der Schalter "zuordnen" ordnet nur gefilterte Punkte dem Projekt zu`,
			confirmText: 'OK',
			confirmClass: 'btn-primary',
			cancelText: ''
		});
	}

	openFilterDialog() {
		if (!this.filterComponentType) return;
		const modalRef = this.modalService.open(this.filterComponentType);
		modalRef.componentInstance.local = this.oms.offlineMode();
		// Aktuell verwendete Filter direkt übergeben
		modalRef.componentInstance.usedFiltersOverride = this.getCurrentUsedFilters();

		// Filter anwenden wenn Event emittiert wird
		modalRef.componentInstance.filterApplied.subscribe((filterName: string) => {
			this.fs.get(filterName).then((conditions) => {
				if (conditions) {
					// Ohne Bounds filtern (alle Punkte)
					this.fs.storeViews(filterName, conditions, false, [], () => this.fs.applyFilter(filterName));
				}
			});
		});

		modalRef.closed.subscribe(() => {
			// Filter-Liste aktualisieren nach Schließen des Dialogs
			this.loadAvailableFilters();
		});
		modalRef.dismissed.subscribe(() => {
			this.loadAvailableFilters();
		});
	}

	private getCurrentUsedFilters(): Set<string> {
		const used = new Set<string>();

		// Globaler Filter
		const globalFilter = this.globalFilterName();
		if (globalFilter) {
			used.add(globalFilter);
		}

		// Region-Filter
		const regionFilters = this.regionFilters();
		Object.values(regionFilters).forEach(f => used.add(f));

		// Bounds-Auswahl-Filter
		this.boundsSelections()
			.filter(bs => bs.filterName)
			.forEach(bs => used.add(bs.filterName!));

		return used;
	}

	zoomToRegion(item: RegionItem) {
		this.ls.regionClicked.emit(item.bounds);
	}

	// === Hilfsmethoden ===

	private async zoomToPoints(pointIds: string[]) {
		if (pointIds.length === 0) return;
		const pointIdSet = new Set(pointIds);
		const response = await this.getCachedPunkte();
		const rows = response.rows;

		let bounds: LatLngBounds | null = null;
		rows.forEach((row: any) => {
			if (pointIdSet.has(row.id)) {
				const header = row.key[4];
				if (header?.koordinaten) {
					const ll = this.ts.from(header.koordinaten);
					if (!bounds) {
						bounds = latLngBounds(ll, ll);
					} else {
						bounds.extend(ll);
					}
				}
			}
		});
		if (bounds) {
			this.ls.regionClicked.emit(bounds);
		}
	}

	someRegionsActive(): boolean {
		return this.regionItems().some(item => item.active);
	}

	allRegionsActive(): boolean {
		const items = this.regionItems();
		return items.length > 0 && items.every(item => item.active);
	}

	/** Gibt die gefilterte Punktanzahl für eine Bounds-Selection zurück */
	getBoundsFilteredCount(index: number, sel: BoundsSelection): number {
		return this.boundsFilteredCounts().get(index) ?? sel.pointIds.length;
	}

	/** Berechnet die Fläche eines Bereichs in Hektar (auf ganze Zahlen gerundet, mit Tausenderpunkt) */
	getRegionAreaHa(item: RegionItem): string {
		const regions = this.ls.regions()?.regions ?? [];
		if (item.index >= regions.length) return '0';

		const region = regions[item.index];
		let areaM2: number;

		if (region.type === 'polygon' && region.polygon) {
			areaM2 = calculatePolygonArea(region.polygon);
		} else {
			areaM2 = calculateBoundsArea(region.bounds);
		}

		// Quadratmeter in Hektar (1 ha = 10.000 m²), auf ganze Zahlen runden
		const ha = Math.round(areaM2 / 10000);
		return ha.toLocaleString('de-DE');
	}

	onClose() {
		this.activeModal.close();
	}

	// === Bereiche-Verwaltung ===

	/** Rechteck-Bereich erstellen - schließt Modal und startet Auswahl auf Karte */
	createRectangleRegion() {
		this.activeModal.close();
		// Warten bis Modal geschlossen ist, damit die Karte wieder aktiv ist
		setTimeout(() => this.ls.customBoundsFromMapRequested.emit(), 100);
	}

	/** Polygon-Bereich erstellen - schließt Modal und startet Zeichnung auf Karte */
	createPolygonRegion() {
		this.activeModal.close();
		// Warten bis Modal geschlossen ist, damit die Karte wieder aktiv ist
		setTimeout(() => this.ls.polygonFromMapRequested.emit(), 100);
	}

	/** Freihand-Bereich erstellen - schließt Modal und startet Freihandzeichnung auf Karte */
	createFreehandRegion() {
		this.activeModal.close();
		// Warten bis Modal geschlossen ist, damit die Karte wieder aktiv ist
		setTimeout(() => this.ls.freehandFromMapRequested.emit(), 100);
	}

	/** Alle Bereiche löschen */
	async deleteAllRegions() {
		const regions = this.ls.regions();
		if (!regions?.regions || regions.regions.length === 0) return;

		const confirmed = await this.confirmService.confirmDelete(
			`Sollen wirklich alle ${regions.regions.length} Bereiche gelöscht werden?`
		);
		if (!confirmed) return;

		// Alle Namen und Filter löschen
		this.regionNames.set({});
		this.regionFilters.set({});

		const newRegions = {
			...regions,
			regions: []
		};

		this.ls.regions.set(newRegions);
		// Regions in DB speichern
		this.ps.setProjectValue('regions', newRegions);
		// Andere Komponenten über Änderung informieren
		this.ls.regionsChanged.emit();
		this.loadRegionItems();
		this.filtersChanged.emit();
	}

	/** Einzelnen Bereich löschen */
	async deleteRegion(item: RegionItem) {
		const regionName = this.getRegionName(item);
		const confirmed = await this.confirmService.confirmDelete(
			`Soll "${regionName}" wirklich gelöscht werden?`
		);
		if (!confirmed) return;

		const regions = this.ls.regions();
		if (!regions?.regions) return;

		const deletedIndex = item.index;
		const totalRegions = regions.regions.length;

		// Namen und Filter neu zuordnen: Alle Indizes > deletedIndex um 1 verringern
		// Dabei explizite Namen für Bereiche setzen, die noch keinen haben (damit der angezeigte Name gleich bleibt)
		const oldNames = this.regionNames();
		const oldFilters = this.regionFilters();
		const newNames: Record<number, string> = {};
		const newFilters: Record<number, string> = {};

		for (let oldIndex = 0; oldIndex < totalRegions; oldIndex++) {
			if (oldIndex === deletedIndex) continue; // Gelöschter Bereich überspringen

			const newIndex = oldIndex < deletedIndex ? oldIndex : oldIndex - 1;

			// Name übernehmen (prüfe beide Schlüsseltypen wegen JSON-Serialisierung)
			const oldName = oldNames[oldIndex] || (oldNames as any)[String(oldIndex)];
			if (oldName) {
				newNames[newIndex] = oldName;
			} else if (oldIndex > deletedIndex) {
				// Bereich hatte keinen expliziten Namen, aber sein Placeholder würde sich ändern
				// Also explizit den alten Placeholder-Namen setzen
				newNames[newIndex] = `Bereich ${oldIndex + 1}`;
			}

			// Filter übernehmen (prüfe beide Schlüsseltypen)
			const oldFilter = oldFilters[oldIndex] || (oldFilters as any)[String(oldIndex)];
			if (oldFilter) {
				newFilters[newIndex] = oldFilter;
			}
		}

		this.regionNames.set(newNames);
		this.regionFilters.set(newFilters);

		const newRegions = {
			...regions,
			regions: regions.regions.filter((_: any, i: number) => i !== deletedIndex)
		};

		this.ls.regions.set(newRegions);
		// Regions in DB speichern
		this.ps.setProjectValue('regions', newRegions);
		// Andere Komponenten über Änderung informieren
		this.ls.regionsChanged.emit();
		this.loadRegionItems();
		this.filtersChanged.emit();
	}

	/** GeoJSON importieren - öffnet File-Picker direkt (User-Gesture-Kontext für Android) */
	importGeoJson() {
		const input = document.createElement('input');
		input.type = 'file';
		input.accept = '.geojson,.json,.gpkg,.zip,.7z,.shp,.dbf,.prj,.shx';
		input.multiple = true;
		input.style.display = 'none';
		document.body.appendChild(input);

		input.onchange = () => {
			if (input.files && input.files.length > 0) {
				this.geoJsonImportRequested.emit(Array.from(input.files));
			}
			document.body.removeChild(input);
			this.activeModal.close();
		};

		// Fallback: Modal schließen wenn Dialog abgebrochen wird
		const handleFocus = () => {
			window.removeEventListener('focus', handleFocus);
			setTimeout(() => {
				if (input.parentNode && !input.files?.length) {
					document.body.removeChild(input);
					// Modal bleibt offen bei Abbruch
				}
			}, 300);
		};
		window.addEventListener('focus', handleFocus);

		input.click();
	}
}
